// admin/src/pages/GatewayMonitoring/components/LoginRelayCard.js
'use strict';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Stack,
  Box,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Grid,
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import DnsIcon from '@mui/icons-material/Dns';

import {
  getAuthRelayStatus,
  approveAuthRelayKey,
  revokeAuthRelayKey,
  extractGatewayErrorMessage,
} from '../gatewayApi';
import { FEATURES, hasFeatureAccess } from '../../../utils/rbac';

function formatRelativeTime(dateInput) {
  if (!dateInput) return 'Never';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return 'Never';
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffSec < 10) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDays = Math.floor(diffHour / 24);
  return `${diffDays}d ago`;
}

function getReadiness(gw) {
  if (!gw) return { ready: false, reasonCode: 'offline', plainReason: 'offline', desc: 'Gateway is offline or degraded', text: 'Not ready (offline)' };

  // Use backend provided relayReadiness if available
  if (gw.relayReadiness && typeof gw.relayReadiness === 'object') {
    const ready = Boolean(gw.relayReadiness.ready);
    const reasonCode = gw.relayReadiness.reason;
    const reasonMap = {
      offline: { plain: 'offline', desc: 'Gateway is offline or degraded' },
      revoked: { plain: 'revoked', desc: 'Relay key is revoked' },
      key_not_approved: { plain: 'key not approved', desc: 'Relay key is not approved' },
      not_polling: { plain: 'not polling', desc: 'Gateway has not polled within the last 45s' },
      directory_unreachable: { plain: 'directory unreachable', desc: 'Directory unreachable' },
      circuit_open: { plain: 'circuit open', desc: 'Circuit open' },
    };
    const info = reasonMap[reasonCode] || { plain: reasonCode ? String(reasonCode).replace(/_/g, ' ') : (ready ? '' : 'not ready'), desc: '' };
    return {
      ready,
      reasonCode,
      plainReason: info.plain,
      desc: info.desc || info.plain,
      text: ready ? 'Ready' : `Not ready (${info.plain})`,
    };
  }

  // Fallback calculation for test or legacy payloads
  const isOnline = String(gw.status || '').toLowerCase() === 'online';
  const isRevoked = String(gw.status || '').toLowerCase() === 'revoked' || gw.relay?.status === 'revoked';
  const isApproved = gw.relay?.status === 'approved';
  const lastPoll = gw.relay?.lastPollAt ? new Date(gw.relay.lastPollAt).getTime() : 0;
  const isPollingActive = lastPoll > 0 && Date.now() - lastPoll <= 45000;
  const isDirReachable = gw.directoryReachable !== false;
  const isCbOpen = String(gw.circuitBreakerState || '').toLowerCase() === 'open';

  if (isRevoked) return { ready: false, reasonCode: 'revoked', plainReason: 'revoked', desc: 'Relay key is revoked', text: 'Not ready (revoked)' };
  if (!isOnline) return { ready: false, reasonCode: 'offline', plainReason: 'offline', desc: 'Gateway is offline or degraded', text: 'Not ready (offline)' };
  if (!isApproved) return { ready: false, reasonCode: 'key_not_approved', plainReason: 'key not approved', desc: 'Relay key is not approved', text: 'Not ready (key not approved)' };
  if (!isPollingActive) return { ready: false, reasonCode: 'not_polling', plainReason: 'not polling', desc: 'Gateway has not polled within the last 45s', text: 'Not ready (not polling)' };
  if (!isDirReachable) return { ready: false, reasonCode: 'directory_unreachable', plainReason: 'directory unreachable', desc: 'Directory unreachable', text: 'Not ready (directory unreachable)' };
  if (isCbOpen) return { ready: false, reasonCode: 'circuit_open', plainReason: 'circuit open', desc: 'Circuit open', text: 'Not ready (circuit open)' };

  return { ready: true, reasonCode: null, plainReason: '', desc: 'Gateway online and actively polling', text: 'Ready' };
}

export default function LoginRelayCard({ currentUser, mode, onRefresh }) {
  const [loading, setLoading] = useState(true);
  const [relayData, setRelayData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [copied, setCopied] = useState(false);

  // Dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [targetGateway, setTargetGateway] = useState(null);
  const [fingerprintInput, setFingerprintInput] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Strict permission gating without role-name checks
  const canUpdate = Boolean(hasFeatureAccess(currentUser, FEATURES.GATEWAYS, 'update'));

  const fetchRelayStatus = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await getAuthRelayStatus();
      if (res && typeof res === 'object') {
        setRelayData(res);
      }
    } catch (err) {
      setErrorMsg(extractGatewayErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRelayStatus();
  }, [fetchRelayStatus]);

  const handleCopyFingerprint = async (text) => {
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const handleOpenApproveDialog = (gw) => {
    setTargetGateway(gw);
    setFingerprintInput(gw?.relay?.fingerprint || '');
    setApproveDialogOpen(true);
    setErrorMsg('');
    setSuccessMsg('');
  };

  const handleOpenRevokeDialog = (gw) => {
    setTargetGateway(gw);
    setRevokeDialogOpen(true);
    setErrorMsg('');
    setSuccessMsg('');
  };

  const handleConfirmApprove = async () => {
    if (!targetGateway?._id || !fingerprintInput.trim()) return;
    setActionLoading(true);
    try {
      await approveAuthRelayKey(targetGateway._id, fingerprintInput.trim());
      setSuccessMsg('Relay key approved successfully.');
      setApproveDialogOpen(false);
      setTargetGateway(null);
      await fetchRelayStatus();
      if (onRefresh) onRefresh();
    } catch (err) {
      setErrorMsg('Approval failed: ' + extractGatewayErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmRevoke = async () => {
    if (!targetGateway?._id) return;
    setActionLoading(true);
    try {
      await revokeAuthRelayKey(targetGateway._id);
      setSuccessMsg('Relay key revoked successfully.');
      setRevokeDialogOpen(false);
      setTargetGateway(null);
      await fetchRelayStatus();
      if (onRefresh) onRefresh();
    } catch (err) {
      setErrorMsg('Revocation failed: ' + extractGatewayErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const gateways = useMemo(() => {
    return Array.isArray(relayData?.gateways) ? relayData.gateways : [];
  }, [relayData]);

  // Next target resolution
  const nextTargetId = useMemo(() => {
    if (relayData?.nextTargetId) return relayData.nextTargetId;
    // Fallback: check routingChain or first eligible gateway
    if (Array.isArray(relayData?.routingChain) && relayData.routingChain.length > 0) {
      return relayData.routingChain[0];
    }
    const eligible = gateways.filter((g) => getReadiness(g).ready);
    if (eligible.length === 0) return null;
    eligible.sort((a, b) => {
      const pA = a.haPriority !== undefined ? a.haPriority : (String(a.haRole || a.ha?.role).toLowerCase() === 'primary' ? 1 : 2);
      const pB = b.haPriority !== undefined ? b.haPriority : (String(b.haRole || b.ha?.role).toLowerCase() === 'primary' ? 1 : 2);
      return pA - pB;
    });
    return eligible[0]._id;
  }, [relayData, gateways]);

  const nextTargetGw = useMemo(() => {
    if (!nextTargetId) return null;
    return gateways.find((g) => g._id === nextTargetId) || null;
  }, [gateways, nextTargetId]);

  // Priority sorted gateways for the failover sequence
  const sortedGatewaysByPriority = useMemo(() => {
    return [...gateways].sort((a, b) => {
      const pA = a.haPriority !== undefined ? a.haPriority : (String(a.haRole || a.ha?.role).toLowerCase() === 'primary' ? 1 : 2);
      const pB = b.haPriority !== undefined ? b.haPriority : (String(b.haRole || b.ha?.role).toLowerCase() === 'primary' ? 1 : 2);
      return pA - pB;
    });
  }, [gateways]);

  const orderSequence = useMemo(() => {
    if (sortedGatewaysByPriority.length === 0) return '';
    return sortedGatewaysByPriority.map((g) => g.name || g._id).join(' > ');
  }, [sortedGatewaysByPriority]);

  // Group gateways by directoryLabel (empty label => "Directory not labelled")
  // Order groups by lowest haPriority inside them
  const gatewayGroups = useMemo(() => {
    if (gateways.length === 0) return [];

    const map = new Map();
    for (const gw of gateways) {
      const rawLabel = (gw.directoryLabel || gw.directory?.label || '').trim();
      const groupKey = rawLabel || 'Directory not labelled';

      if (!map.has(groupKey)) {
        map.set(groupKey, []);
      }
      map.get(groupKey).push(gw);
    }

    const groups = [];
    for (const [label, gws] of map.entries()) {
      gws.sort((a, b) => {
        const pA = a.haPriority !== undefined ? a.haPriority : (String(a.haRole || a.ha?.role).toLowerCase() === 'primary' ? 1 : 2);
        const pB = b.haPriority !== undefined ? b.haPriority : (String(b.haRole || b.ha?.role).toLowerCase() === 'primary' ? 1 : 2);
        return pA - pB;
      });

      const lowestPriority = Math.min(
        ...gws.map((g) => (g.haPriority !== undefined ? g.haPriority : (String(g.haRole || g.ha?.role).toLowerCase() === 'primary' ? 1 : 2)))
      );

      const hosts = Array.from(
        new Set(gws.map((g) => (g.directoryHost || g.directory?.host || '').trim()).filter(Boolean))
      );

      const isNextTargetGroup = Boolean(nextTargetId && gws.some((g) => g._id === nextTargetId));

      groups.push({
        label,
        lowestPriority,
        hosts,
        isNextTargetGroup,
        gateways: gws,
      });
    }

    groups.sort((a, b) => a.lowestPriority - b.lowestPriority);
    return groups;
  }, [gateways, nextTargetId]);

  const isRelayEnabled = relayData?.enabled !== false;

  return (
    <Card
      elevation={0}
      data-testid="ad-login-relay-card"
      sx={{
        border: '1px solid var(--hairline, #E4E9F0)',
        borderRadius: '8px',
        mb: 3,
        overflow: 'hidden',
      }}
    >
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <VpnKeyIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
              Active Directory Login Relay
            </Typography>
          </Stack>
        }
        action={
          <IconButton
            size="small"
            onClick={fetchRelayStatus}
            disabled={loading}
            aria-label="Refresh relay status"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        }
        sx={{
          borderBottom: '1px solid var(--hairline, #E4E9F0)',
          py: 1.5,
          px: 2.5,
          bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#FAFCFF',
        }}
      />

      <CardContent sx={{ p: 2.5 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}

        {!loading && successMsg && (
          <Alert severity="success" sx={{ mb: 2 }} data-testid="relay-success-alert">
            {successMsg}
          </Alert>
        )}

        {!loading && !isRelayEnabled && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            data-testid="relay-disabled-banner"
            sx={{ borderRadius: '6px' }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Relay Disabled
            </Typography>
            <Typography variant="body2">
              AD login relay is currently disabled via environment configuration (AD_AUTH_RELAY_ENABLED is false).
            </Typography>
          </Alert>
        )}

        {!loading && isRelayEnabled && gateways.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No gateways registered for authentication relay.
          </Typography>
        )}

        {!loading && isRelayEnabled && gateways.length > 0 && (
          <Stack spacing={2.5}>
            {/* Banner: which gateway handles the next login, or fail closed warning, plus one-line sequence */}
            <Alert
              severity={nextTargetGw ? 'success' : 'error'}
              icon={nextTargetGw ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
              data-testid="ha-routing-banner"
              sx={{ borderRadius: '6px' }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {nextTargetGw
                  ? String(nextTargetGw.haRole || nextTargetGw.ha?.role).toLowerCase() === 'primary'
                    ? `Active Target: Primary (${nextTargetGw.name})`
                    : `Failover Active: Secondary (${nextTargetGw.name})`
                  : 'No gateway is relay-eligible'}
              </Typography>
              <Typography variant="body2">
                {nextTargetGw
                  ? `Next login handled by ${nextTargetGw.name} (${String(nextTargetGw.haRole || nextTargetGw.ha?.role || 'primary').toUpperCase()}).`
                  : 'Active Directory logins are failing closed.'}
              </Typography>
              {orderSequence && (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 0.5, fontWeight: 600, opacity: 0.9 }}
                  data-testid="ha-routing-order"
                >
                  Order: {orderSequence}
                </Typography>
              )}
            </Alert>

            {/* Container for gateway groups */}
            <Box data-testid="dual-relay-container">
              <Stack spacing={3}>
                {gatewayGroups.map((group) => {
                  return (
                    <Paper
                      key={group.label}
                      variant="outlined"
                      data-testid={`gateway-group-${group.label.replace(/\s+/g, '-').toLowerCase()}`}
                      sx={{
                        p: 2,
                        borderRadius: '8px',
                        border: '1px solid var(--hairline, #E4E9F0)',
                        bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.01)' : '#FFFFFF',
                      }}
                    >
                      {/* Group Header: Label, DC host(s), and Standby badge if not target group */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: 1,
                          mb: 2,
                          pb: 1.5,
                          borderBottom: '1px solid var(--hairline, #E4E9F0)',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                          <DnsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {group.label}
                          </Typography>
                          {group.hosts.length > 0 && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`DC: ${group.hosts.join(', ')}`}
                              sx={{ fontSize: 11, height: 22 }}
                            />
                          )}
                        </Stack>

                        {!group.isNextTargetGroup && (
                          <Chip
                            size="small"
                            label="Standby"
                            color="default"
                            data-testid="group-standby-badge"
                            sx={{
                              fontWeight: 700,
                              fontSize: 11,
                              height: 22,
                              bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#F1F3F5',
                            }}
                          />
                        )}
                      </Box>

                      {/* Gateways in this group */}
                      <Grid container spacing={2}>
                        {group.gateways.map((gw) => {
                          const isPrimary = String(gw.haRole || gw.ha?.role || '').toLowerCase() === 'primary';
                          const isNextTarget = gw._id === nextTargetId;
                          const readiness = getReadiness(gw);
                          const priorityNum = gw.haPriority !== undefined ? gw.haPriority : (isPrimary ? 1 : 2);

                          return (
                            <Grid item xs={12} md={group.gateways.length === 1 ? 12 : 6} key={gw._id}>
                              <Paper
                                variant="outlined"
                                data-testid={
                                  isPrimary
                                    ? 'primary-relay-card'
                                    : (group.gateways.length === 2 && !isPrimary ? 'secondary-relay-card' : `gateway-relay-card-${gw._id}`)
                                }
                                sx={{
                                  p: 2,
                                  borderRadius: '6px',
                                  border: '1px solid',
                                  borderColor: isNextTarget ? 'primary.main' : 'divider',
                                  bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#FAFCFF',
                                  position: 'relative',
                                }}
                              >
                                <Stack spacing={1.5}>
                                  {/* Row 1: Name + Role Chip + Next Target Badge */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap title={gw.name}>
                                        {gw.name}
                                      </Typography>
                                      {isNextTarget && (
                                        <Chip
                                          size="small"
                                          label="Next target"
                                          color="success"
                                          data-testid="next-target-badge"
                                          sx={{ fontWeight: 800, fontSize: 10, height: 20 }}
                                        />
                                      )}
                                    </Stack>

                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Chip
                                        size="small"
                                        label={isPrimary ? 'PRIMARY' : 'SECONDARY'}
                                        data-testid={isPrimary ? 'primary-role-chip' : 'secondary-role-chip'}
                                        sx={{
                                          fontSize: '0.65rem',
                                          fontWeight: 700,
                                          height: 22,
                                          bgcolor: isPrimary
                                            ? (mode === 'dark' ? 'rgba(31, 111, 235, 0.2)' : '#EBF5FF')
                                            : (mode === 'dark' ? 'rgba(139, 148, 158, 0.2)' : '#F3F4F6'),
                                          color: isPrimary
                                            ? (mode === 'dark' ? '#58A6FF' : '#0969DA')
                                            : (mode === 'dark' ? '#C9D1D9' : '#57606A'),
                                          border: '1px solid',
                                          borderColor: isPrimary
                                            ? (mode === 'dark' ? '#1F6FEB' : '#B6E3FF')
                                            : (mode === 'dark' ? '#30363D' : '#D0D7DE'),
                                        }}
                                      />
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`Priority ${priorityNum}`}
                                        data-testid={`gateway-priority-chip-${gw._id}`}
                                        sx={{ fontSize: 11, height: 22, fontWeight: 600 }}
                                      />
                                    </Stack>
                                  </Box>

                                  {/* Row 2: Status Chips */}
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                                    <Chip
                                      size="small"
                                      label={`Status: ${gw.status || 'Offline'}`}
                                      color={String(gw.status).toLowerCase() === 'online' ? 'success' : 'default'}
                                      sx={{ fontSize: 11, height: 22 }}
                                    />
                                    <Chip
                                      size="small"
                                      data-testid="relay-readiness-chip"
                                      label={readiness.ready ? 'Relay Active' : 'Relay Idle'}
                                      color={readiness.ready ? 'success' : 'default'}
                                      icon={readiness.ready ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />}
                                      sx={{ fontSize: 11, height: 22 }}
                                    />
                                    <Chip
                                      size="small"
                                      data-testid={`gateway-readiness-chip-${gw._id}`}
                                      label={readiness.ready ? 'Ready' : `Not ready (${readiness.plainReason})`}
                                      color={readiness.ready ? 'success' : 'default'}
                                      sx={{ fontSize: 11, height: 22 }}
                                    />
                                    <Chip
                                      size="small"
                                      label={`Key: ${gw.relay?.status || 'unregistered'}`}
                                      color={gw.relay?.status === 'approved' ? 'success' : (gw.relay?.status === 'pending' ? 'warning' : 'default')}
                                      sx={{ fontSize: 11, height: 22 }}
                                    />
                                  </Stack>

                                  {/* Row 3: Host + Readiness Details */}
                                  <Box sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                    <Typography variant="caption" display="block">
                                      Host: <strong>{gw.directoryHost || gw.directory?.host || '—'}</strong>
                                    </Typography>
                                    <Typography variant="caption" display="block" data-testid={`readiness-detail-${gw._id}`}>
                                      Readiness:{' '}
                                      <strong>
                                        {readiness.ready
                                          ? 'Ready (Gateway online and actively polling)'
                                          : `Not ready (${readiness.plainReason}): ${readiness.desc}`}
                                      </strong>
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                      Last poll: <strong>{formatRelativeTime(gw.relay?.lastPollAt)}</strong>
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                      Last served: <strong>{formatRelativeTime(gw.lastRelayServedAt)}</strong>
                                    </Typography>
                                  </Box>

                                  {/* Row 4: Pending key message if applicable */}
                                  {gw.relay?.status === 'pending' && (
                                    <Alert severity="warning" data-testid="relay-status-pending" sx={{ py: 0, px: 1, fontSize: '0.75rem' }}>
                                      New key awaiting approval
                                    </Alert>
                                  )}

                                  {/* Row 5: Fingerprint display */}
                                  {gw.relay?.fingerprint && (
                                    <Box sx={{ p: 1, borderRadius: 1, bgcolor: mode === 'dark' ? 'rgba(0,0,0,0.3)' : '#F0F4F8' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">
                                          Fingerprint:
                                        </Typography>
                                        <Tooltip title={copied ? 'Copied!' : 'Copy Fingerprint'}>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleCopyFingerprint(gw.relay.fingerprint)}
                                            aria-label="Copy fingerprint"
                                          >
                                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                      <Typography
                                        variant="caption"
                                        data-testid="relay-fingerprint-text"
                                        sx={{ fontFamily: 'Consolas, monospace', wordBreak: 'break-all', display: 'block' }}
                                      >
                                        {gw.relay.fingerprint}
                                      </Typography>
                                    </Box>
                                  )}

                                  {/* Row 6: Action Buttons */}
                                  {canUpdate && (
                                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 0.5 }}>
                                      {gw.relay?.status === 'pending' && (
                                        <>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            data-testid={isPrimary ? 'primary-approve-key-btn' : (group.gateways.length === 2 && !isPrimary ? 'secondary-approve-key-btn' : 'approve-key-btn')}
                                            onClick={() => handleOpenApproveDialog(gw)}
                                          >
                                            Approve Key
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            data-testid="reject-key-btn"
                                            onClick={() => handleOpenRevokeDialog(gw)}
                                          >
                                            Reject
                                          </Button>
                                        </>
                                      )}
                                      {gw.relay?.status === 'approved' && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="error"
                                          data-testid={isPrimary ? 'primary-revoke-key-btn' : (group.gateways.length === 2 && !isPrimary ? 'secondary-revoke-key-btn' : 'revoke-key-btn')}
                                          onClick={() => handleOpenRevokeDialog(gw)}
                                        >
                                          Revoke Key
                                        </Button>
                                      )}
                                    </Stack>
                                  )}
                                </Stack>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        )}
      </CardContent>

      {/* Approve Relay Key Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => !actionLoading && setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="approve-key-dialog"
      >
        <DialogTitle>Approve Relay Key</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Verify the cryptographic fingerprint presented by{' '}
            <strong>
              {targetGateway?.name || 'Gateway'}{' '}
              {targetGateway?.haRole ? `(${targetGateway.haRole.charAt(0).toUpperCase() + targetGateway.haRole.slice(1)})` : ''}
            </strong>{' '}
            matches the key generated on the host before approving.
          </DialogContentText>
          <TextField
            autoFocus
            label="Key Fingerprint"
            fullWidth
            value={fingerprintInput}
            onChange={(e) => setFingerprintInput(e.target.value)}
            data-testid="approve-fingerprint-input"
            helperText="Confirm or paste the 64-character SHA-256 fingerprint"
            InputProps={{
              sx: { fontFamily: 'Consolas, monospace', fontSize: 13 },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmApprove}
            disabled={actionLoading || !fingerprintInput.trim()}
            data-testid="confirm-approve-btn"
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            {actionLoading ? 'Approving...' : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Relay Key Dialog */}
      <Dialog
        open={revokeDialogOpen}
        onClose={() => !actionLoading && setRevokeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="revoke-key-dialog"
      >
        <DialogTitle sx={{ color: 'error.main' }}>Revoke Relay Key</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke the relay key for{' '}
            <strong>
              {targetGateway?.name || 'Gateway'}{' '}
              {targetGateway?.haRole ? `(${targetGateway.haRole.charAt(0).toUpperCase() + targetGateway.haRole.slice(1)})` : ''}
            </strong>
            ? This will immediately prevent this gateway from decrypting authentication requests.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRevoke}
            disabled={actionLoading}
            data-testid="confirm-revoke-btn"
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            {actionLoading ? 'Revoking...' : 'Revoke Key'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
